/**
 * "This blur came from Unity, do not commit" (ADR-049 D6).
 *
 * ## Why this exists at all
 *
 * The plan panel found that honouring Unity's `blurEditor` with a plain `activeElement.blur()` would **write**:
 * `TextSettingControl.onBlur` calls `onSet("Text", …)` whenever its draft differs from the server value, so a
 * cross-origin message would persist a pending edit through the DOM's own event path — reaching a mutation the
 * bridge never imported. D2's "the handler has no API access" would have been literally true and completely
 * beside the point.
 *
 * ## Why a synchronous flag is right HERE, when D5 rejected exactly that shape
 *
 * D5 refuses the legacy's `applyingRemoteDelete`-style module flags because they fence a region that is only
 * synchronous in the legacy: in React a state update is deferred, so a flag cleared at the end of a handler is
 * cleared before the render that would echo.
 *
 * **`blur()` is different, and the difference is the whole justification.** Dispatching a blur event is
 * synchronous — `HTMLElement.blur()` runs every `onBlur` handler before it returns. So the flag is set, the
 * handlers run, and it is cleared, all within one call stack that cannot interleave. `try/finally` makes that
 * true even if a handler throws.
 *
 * Phase 1 has no write wiring, so nothing reads this yet. It ships now on purpose: the commit path lands in
 * ADR-044 Phase 2, and a guard that arrives after the thing it guards is a guard that was missing once.
 */
let remoteBlurDepth = 0;

/** True while a blur triggered by Unity is being dispatched. Commit-on-blur handlers MUST consult this. */
export function isRemoteBlur(): boolean {
  return remoteBlurDepth > 0;
}

/**
 * Run `fn` with {@link isRemoteBlur} true. Re-entrant and exception-safe; the flag can never be left set.
 */
export function duringRemoteBlur(fn: () => void): void {
  remoteBlurDepth += 1;
  try {
    fn();
  } finally {
    remoteBlurDepth -= 1;
  }
}

/**
 * Drop focus from whatever holds it, without committing a pending draft.
 *
 * `doc` is injectable for tests. A missing or unblurrable `activeElement` is a no-op, not an error — Unity sends
 * this on every canvas click, most of which land when nothing in the panel is focused at all.
 */
export function blurActiveElementWithoutCommit(doc: Document = document): void {
  const el = doc.activeElement;
  if (!el || !(el instanceof HTMLElement) || el === doc.body) return;
  duringRemoteBlur(() => el.blur());
}
